import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { cn } from '../../lib/cn';
import { ExternalAnchor } from '../ui/Link';

/**
 * Tiny, safe markdown renderer for versioned content (@seventide/content). Produces React elements only
 * (no innerHTML). Supports: #/##/### headings (rendered one level below `baseHeading`), paragraphs,
 * "-"/"*" and "1." lists, **bold**, *italic* / _italic_, [links](url) and `code`.
 */
export function Markdown({ text, baseHeading = 3, className }: { text: string; baseHeading?: 2 | 3 | 4; className?: string }) {
  const blocks = parseBlocks(text);
  return (
    <div className={cn('flex flex-col gap-3 leading-relaxed', className)}>
      {blocks.map((b, i) => {
        switch (b.type) {
          case 'heading': {
            const level = Math.min(6, baseHeading + b.level - 1);
            const H = `h${level}` as 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
            return (
              <H key={i} className="text-lg font-bold">
                {inline(b.text)}
              </H>
            );
          }
          case 'ul':
            return (
              <ul key={i} className="ml-6 list-disc space-y-1">
                {b.items.map((it, j) => (
                  <li key={j}>{inline(it)}</li>
                ))}
              </ul>
            );
          case 'ol':
            return (
              <ol key={i} className="ml-6 list-decimal space-y-1">
                {b.items.map((it, j) => (
                  <li key={j}>{inline(it)}</li>
                ))}
              </ol>
            );
          default:
            return <p key={i}>{inline(b.text)}</p>;
        }
      })}
    </div>
  );
}

type Block =
  | { type: 'heading'; level: number; text: string }
  | { type: 'p'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] };

function parseBlocks(src: string): Block[] {
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  const out: Block[] = [];
  // Mutable parse state kept on an object so TypeScript doesn't narrow it across helper calls.
  const st: { para: string[]; list: { type: 'ul' | 'ol'; items: string[] } | null } = { para: [], list: null };
  const flushPara = () => {
    if (st.para.length) out.push({ type: 'p', text: st.para.join(' ') });
    st.para = [];
  };
  const flushList = () => {
    if (st.list) out.push(st.list);
    st.list = null;
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    const ul = /^\s*[-*]\s+(.*)$/.exec(line);
    const ol = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (!line.trim()) {
      flushPara();
      flushList();
    } else if (h) {
      flushPara();
      flushList();
      out.push({ type: 'heading', level: h[1]?.length ?? 1, text: h[2] ?? '' });
    } else if (ul || ol) {
      flushPara();
      const type = ul ? 'ul' : 'ol';
      if (!st.list || st.list.type !== type) {
        flushList();
        st.list = { type, items: [] };
      }
      st.list.items.push((ul ? ul[1] : ol?.[1]) ?? '');
    } else if (st.list && /^\s{2,}/.test(raw)) {
      // continuation of a list item
      const items = st.list.items;
      items[items.length - 1] += ` ${line.trim()}`;
    } else {
      flushList();
      st.para.push(line.trim());
    }
  }
  flushPara();
  flushList();
  return out;
}

const INLINE = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)\s]+\)|`[^`]+`|\*[^*\s][^*]*\*|_[^_\s][^_]*_)/g;

function inline(text: string): ReactNode[] {
  const parts = text.split(INLINE).filter((p) => p !== '');
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (p.startsWith('`') && p.endsWith('`')) return <code key={i}>{p.slice(1, -1)}</code>;
    const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(p);
    if (link) {
      const [, label = '', href = ''] = link;
      if (/^https?:\/\//.test(href)) {
        return (
          <ExternalAnchor key={i} href={href}>
            {label}
          </ExternalAnchor>
        );
      }
      if (href.startsWith('/')) {
        return (
          <RouterLink key={i} to={href} className="font-bold text-primary underline underline-offset-4">
            {label}
          </RouterLink>
        );
      }
      return <span key={i}>{label}</span>;
    }
    if ((p.startsWith('*') && p.endsWith('*')) || (p.startsWith('_') && p.endsWith('_'))) {
      return <em key={i}>{p.slice(1, -1)}</em>;
    }
    return <span key={i}>{p}</span>;
  });
}
